import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { LookupService } from "src/app/services/lookup.service";
import { Beneficiary, BeneficiaryPayload, BenefitBeneficiaryCard } from "src/app/models/beneficiary";
import { ListItem } from "src/app/models/listItem";
import { BeneficiariesService } from "src/app/services/beneficiaries.service";

@Component({
    selector: 'mwp-beneficiaries-form',
    templateUrl: './beneficiaries-form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BeneficiariesFormComponent implements OnInit {
    @Input() public benefitBeneficiaryCard: BenefitBeneficiaryCard;
    @Input() public beneficiaries: Beneficiary[];
    @Input() public isWizardComponent: boolean;
    @Input() public isEmitPaylod: boolean;
    @Input() public isModal: boolean;
    @Input() public isEnrollment: boolean;
    @Input() public hideWizardCancelButton: boolean;
    @Input() public saveButtonText: string;
    @Output() public emitSaveCardModal: EventEmitter<Beneficiary[]> = new EventEmitter();
    @Output() public emitSaveCard: EventEmitter<BenefitBeneficiaryCard> = new EventEmitter();
    @Output() public emitSavePayload: EventEmitter<BeneficiaryPayload> = new EventEmitter();
    @Output() public emitCancel: EventEmitter<any> = new EventEmitter();

    public relationshipsList: ListItem[];
    public beneficiariesForm: FormGroup;
    public memberBenefitID: number;
    public beneficiaryListArrayName: string = 'beneficiaryList';
    public percentageName: string = 'tempPercentage';
    public isPrimaryName: string = 'isPrimary';
    public isContingentName: string = 'isContingent';

    constructor(
        private formBuilder: FormBuilder,
        private lookupService: LookupService,
        private beneficiariesService: BeneficiariesService,
    ) {
    }

    ngOnInit() {
        this.lookupService.getBeneficiaryRelationships().subscribe((response: ListItem[]) => {
            this.relationshipsList = response;
        });

        let l_FormArray_Beneficiary: FormArray = new FormArray([]);
        let l_Data: Beneficiary[];

        if (this.benefitBeneficiaryCard) {
            l_Data = this.benefitBeneficiaryCard.beneficiariesForDisplay;
        } else if (this.beneficiaries) {
            l_Data = this.beneficiaries;
        }

        if (l_Data[0]?.memberBenefitID)
            this.memberBenefitID = l_Data[0].memberBenefitID;
        else if (this.benefitBeneficiaryCard?.memberBenefitID)
            this.memberBenefitID = this.benefitBeneficiaryCard.memberBenefitID;

        if (l_Data.length > 0) {
            l_Data.forEach((bfd) => {
                l_FormArray_Beneficiary.push(
                    this.formBuilder.group({
                        memberBenefitBeneficiaryID: [bfd.memberBenefitBeneficiaryID],
                        firstName: [bfd.firstName, Validators.required],
                        lastName: [bfd.lastName, Validators.required],
                        relationship: [bfd.relationship, Validators.required],
                        [this.percentageName]: [bfd.percentage * 100, Validators.required],
                        isSelected: [bfd.isSelected],
                        [this.isPrimaryName]: [{ value: bfd.isPrimary, disabled: !bfd.isPrimary }],
                        [this.isContingentName]: [{ value: bfd.isContingent, disabled: !bfd.isContingent }],
                        beneficiaryIDNumber: [bfd.beneficiaryIDNumber],
                        countryOfCitizenship: [bfd.countryOfCitizenship],
                        memberBenefitID: [bfd.memberBenefitID]
                    })
                );
            });
        } else {
            const l_Beneficiary: Beneficiary = new Beneficiary();
            l_Beneficiary.memberBenefitID = this.memberBenefitID;
            l_FormArray_Beneficiary.push(
                this.formBuilder.group({
                    memberBenefitBeneficiaryID: [l_Beneficiary.memberBenefitBeneficiaryID],
                    firstName: [l_Beneficiary.firstName],
                    lastName: [l_Beneficiary.lastName],
                    relationship: [l_Beneficiary.relationship],
                    [this.percentageName]: [l_Beneficiary.percentage],
                    isSelected: [l_Beneficiary.isSelected],
                    [this.isPrimaryName]: [{ value: l_Beneficiary.isPrimary, disabled: true }],
                    [this.isContingentName]: [{ value: l_Beneficiary.isContingent, disabled: true }],
                    beneficiaryIDNumber: [l_Beneficiary.beneficiaryIDNumber],
                    countryOfCitizenship: [l_Beneficiary.countryOfCitizenship],
                    memberBenefitID: [l_Beneficiary.memberBenefitID]
                })
            );
        }

        this.beneficiariesForm = this.formBuilder.group({
            [this.beneficiaryListArrayName]: l_FormArray_Beneficiary
        });
    }

    get gridArray(): FormArray {
        return (<FormArray>(this.beneficiariesForm.get(this.beneficiaryListArrayName)));
    }

    get gridArrayControls(): AbstractControl[] {
        return this.gridArray.controls;
    }

    public addRow(): void {
        const l_Beneficiary: Beneficiary = new Beneficiary();
        l_Beneficiary.memberBenefitID = this.memberBenefitID;
        this.gridArray.push(
            this.formBuilder.group({
                memberBenefitBeneficiaryID: [l_Beneficiary.memberBenefitBeneficiaryID],
                firstName: [l_Beneficiary.firstName],
                lastName: [l_Beneficiary.lastName],
                relationship: [l_Beneficiary.relationship],
                [this.percentageName]: [l_Beneficiary.percentage],
                isSelected: [l_Beneficiary.isSelected],
                [this.isPrimaryName]: [{ value: l_Beneficiary.isPrimary, disabled: true }],
                [this.isContingentName]: [{ value: l_Beneficiary.isContingent, disabled: true }],
                beneficiaryIDNumber: [l_Beneficiary.beneficiaryIDNumber],
                countryOfCitizenship: [l_Beneficiary.countryOfCitizenship],
                memberBenefitID: [l_Beneficiary.memberBenefitID]
            })
        );
    }

    public selectedGrid(event: MatCheckboxChange, gridId: number): void {
        const l_GridControl: AbstractControl = this.gridArray.at(gridId);
        const l_PrimaryControl: AbstractControl = l_GridControl.get(this.isPrimaryName);
        const l_ContingentControl: AbstractControl = l_GridControl.get(this.isContingentName);

        this.toggleCheckBoxControl(l_PrimaryControl, event.checked);
        this.toggleCheckBoxControl(l_ContingentControl, event.checked);

        this.toggleControlValidation(l_GridControl, 'firstName', event.checked);
        this.toggleControlValidation(l_GridControl, 'lastName', event.checked);
        this.toggleControlValidation(l_GridControl, 'relationship', event.checked);
        this.toggleControlValidation(l_GridControl, this.percentageName, event.checked);
    }

    private toggleControlValidation(gridControl: AbstractControl, controlName: string, isChecked: boolean) {
        const l_Control = gridControl.get(controlName);
        if (isChecked)
            l_Control.setValidators(Validators.required);
        else
            l_Control.clearValidators();

        l_Control.updateValueAndValidity();
    }

    public selectedPrimary(event: MatCheckboxChange, gridId: number): void {
        const l_GridControl: AbstractControl = this.gridArray.at(gridId);
        let l_Control: AbstractControl = l_GridControl.get(this.isContingentName);
        this.toggleCheckBoxControl(l_Control, !event.checked);
    }

    public selectedContingent(event: MatCheckboxChange, gridId: number): void {
        const l_GridControl: AbstractControl = this.gridArray.at(gridId);
        let l_Control: AbstractControl = l_GridControl.get(this.isPrimaryName);
        this.toggleCheckBoxControl(l_Control, !event.checked);
    }

    private toggleCheckBoxControl(control: AbstractControl, isChecked: boolean): void {
        control.setValue(false);
        if (isChecked)
            control.enable();
        else
            control.disable();
    }

    public cancelForm(): void {
        this.emitCancel.emit();
    }

    public completeForm(): void {
        const l_BeneficiaryForm: BeneficiaryPayload = this.beneficiariesForm.value;
        const l_Payload: BeneficiaryPayload = this.beneficiariesService.beneficariesPaylodValidation(l_BeneficiaryForm);
        if (l_Payload) {
            l_Payload.beneficiaryList.forEach((beneficiary: Beneficiary) => {
                beneficiary.percentage = beneficiary.percentage / 100;
            });
            if (this.isEmitPaylod) {
                this.emitSavePayload.emit(l_Payload);
            } if (this.isModal) {
                this.emitSaveCardModal.emit(l_Payload.beneficiaryList);
            } else {
                this.benefitBeneficiaryCard.beneficiariesForDisplay = l_Payload.beneficiaryList;
                this.emitSaveCard.emit(this.benefitBeneficiaryCard);
            }
        }
    }
}